import React, { useEffect, useRef, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { isSameCourse } from './CourseCard';
import CourseCompactCard from './CourseCompactCard';
import { CourseMinifiedDTO } from '../../services/apiClient';

interface NearbyCoursesStripProps {
  courses: (CourseMinifiedDTO & { distanceMeters: number })[];
  selectedCourseId?: string;
  // Centres the map on the course. Driven by scrolling the strip, not by tapping a card: the card
  // that comes to rest at the left edge becomes the selected one. It does not open the detail
  // drawer, because on a phone that drawer covers the map the visitor is browsing.
  onCourseClick: (course: CourseMinifiedDTO) => void;
  // Tapping a card. Opens the detail drawer on that course, which is where the hero, the phone
  // number, Share and the bucket-list checkbox live. The whole card is the target now, so this no
  // longer belongs to a View button in the corner.
  onViewCourse: (course: CourseMinifiedDTO) => void;
  // Played is set straight from the card, without opening anything. Explore puts it behind the
  // sign-in gate, so a signed-out visitor gets the member-setup drawer instead of a tick.
  isCoursePlayed: (course: CourseMinifiedDTO) => boolean;
  onTogglePlayed: (course: CourseMinifiedDTO) => void;
  originLabel: string;
  // Identity of whatever the list is sorted against — a course id, or `user` while the strip is
  // following the visitor. Deliberately not derived from originLabel: course names are not unique
  // across 41k rows, so hopping between two identically-named courses would look like no change
  // at all and the strip would keep its old scroll position.
  originKey: string;
  /**
   * Reports this strip's rendered height in pixels, and 0 on the way out. Explore uses it to keep
   * the map's locate button just above the strip.
   *
   * Reported rather than assumed, because the height is content-driven: a card with an address
   * over two lines is taller than one without, so the offset that looked right against one club
   * put the button inside the strip against another. Must be stable (useCallback), because the
   * observer below re-subscribes when it changes.
   */
  onHeightChange?: (height: number) => void;
}

const NearbyCoursesStrip: React.FC<NearbyCoursesStripProps> = ({
  courses,
  selectedCourseId,
  onCourseClick,
  onViewCourse,
  isCoursePlayed,
  onTogglePlayed,
  originLabel,
  originKey,
  onHeightChange,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // CSS scroll-snap fights a directly-assigned scrollLeft during drag, making the strip jump
  // straight to the snap point instead of following the cursor — snapping is switched off for
  // the duration of the drag (see className below) and reapplied with an animated settle once
  // the pointer is released, so dragging reads as a real slide rather than an instant jump.
  const [isDragging, setIsDragging] = useState(false);

  // Mouse-drag scrolling (touch already scrolls natively) — plain overflow-x-auto gives
  // desktop mouse users no obvious way to move the strip at all, so this makes click-and-drag
  // work like a real carousel. dragged tracks total movement so a drag doesn't also fire the
  // card's onClick underneath it.
  const dragState = useRef({
    isDown: false,
    startX: 0,
    startScrollLeft: 0,
    dragged: false,
  });

  // Scrolling the strip selects whatever card sits at its left edge, so the map keeps up with
  // the visitor's thumb instead of waiting for a tap. Throttled to one measurement per painted
  // frame rather than debounced: a debounce, even a short one, split the gesture into three
  // visible steps — scroll, then the card highlights, then the map moves. Reacting while the
  // strip is still moving collapses those back into one motion.
  const scrollFrame = useRef<number | null>(null);
  // One scroll is not the visitor's doing: the reset to card 0 when the origin changes. Auto
  // select has to sit that one out, or changing origin would immediately fly the map to the
  // nearest course and re-open a selection the visitor never asked for.
  const skipNextAutoSelect = useRef(false);
  // The card the strip is sliding to because a course was selected from outside it (a pin tap on
  // the map). Auto select holds off until the strip gets there, or every card passed on the way
  // would be selected in turn and the map would fly through each of them.
  const slideTargetIndex = useRef<number | null>(null);

  // The strip's own left padding means its natural resting position at the very start is
  // ~16px, not 0 — the threshold has to clear that or the left arrow shows up immediately.
  const START_SCROLL_SLACK = 20;

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setCanScrollLeft(el.scrollLeft > START_SCROLL_SLACK);
    setCanScrollRight(
      el.scrollLeft + el.clientWidth < el.scrollWidth - START_SCROLL_SLACK,
    );
  };

  useEffect(() => {
    updateScrollState();
  }, [courses]);

  // Keep Explore told how tall this is. A ResizeObserver rather than a measurement on mount: the
  // cards arrive with their logos and addresses after the first paint, and a card whose address
  // wraps to a second line changes this height without anything here re-rendering.
  //
  // Reports 0 on unmount so the offset it feeds collapses with the strip rather than leaving the
  // button parked over empty map.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !onHeightChange) {
      return;
    }

    const report = (): void => onHeightChange(el.offsetHeight);
    report();

    const observer = new ResizeObserver(report);
    observer.observe(el);
    return (): void => {
      observer.disconnect();
      onHeightChange(0);
    };
  }, [onHeightChange]);

  // A new origin means an entirely different list, so the strip has to go back to the first card.
  // Without this, picking a course in another province leaves the visitor parked at card 20 of a
  // list they have never seen — the cards under the viewport changed, the scroll position did not.
  //
  // Keyed on originKey, not on the courses array: while geolocation is tracking, every GPS fix
  // rebuilds courses, and yanking the scroll back to the start under the visitor’s finger on each
  // fix would be worse than never resetting at all.
  //
  // Instant rather than smooth — these are different courses now, so animating across them would
  // read as travel through a list that no longer exists.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      skipNextAutoSelect.current = true;
      el.scrollLeft = 0;
    }
    updateScrollState();
  }, [originKey]);

  const scrollByCards = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  // Whichever card currently sits closest to the strip's left edge, as both its index and how
  // far it is from that edge. One measurement pass serves the drag settle and the auto select.
  const findNearestCard = (el: HTMLDivElement) => {
    const elRect = el.getBoundingClientRect();
    let nearestOffset = 0;
    let nearestIndex = 0;
    let minDiff = Infinity;
    Array.from(el.children).forEach((child, index) => {
      const childRect = (child as HTMLElement).getBoundingClientRect();
      const offset = childRect.left - elRect.left;
      const diff = Math.abs(offset);
      if (diff < minDiff) {
        minDiff = diff;
        nearestOffset = offset;
        nearestIndex = index;
      }
    });
    return { nearestOffset, nearestIndex };
  };

  // After a drag ends, glide to whichever card sits closest to the release point instead of
  // relying on scroll-snap to just teleport there.
  const settleToNearestCard = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollTo({
      left: el.scrollLeft + findNearestCard(el).nearestOffset,
      behavior: 'smooth',
    });
  };

  // Select whichever card currently holds the left edge. Selecting only centres the map (see the
  // onCourseClick contract above), it does not open the detail drawer and does not re-sort the
  // list, so it is safe to fire mid-scroll: the card under the thumb never moves.
  const selectNearestCard = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    if (skipNextAutoSelect.current) {
      skipNextAutoSelect.current = false;
      return;
    }
    const { nearestIndex } = findNearestCard(el);
    if (slideTargetIndex.current !== null) {
      if (nearestIndex === slideTargetIndex.current) {
        slideTargetIndex.current = null;
      }
      return;
    }
    const course = courses[nearestIndex];
    // Already the selected one, so there is nothing to fly to. This is the common case now, not
    // the rare one: most frames of a swipe are still over the card the previous frame picked, so
    // this guard is what keeps a per-frame handler from restarting the map animation constantly.
    if (!course || isSameCourse(course.id, selectedCourseId)) {
      return;
    }
    onCourseClick(course);
  };

  // A course selected from outside the strip (a pin tap on the map) slides its card to the left
  // edge. A selection the strip made itself is already there, so that case does nothing. Waits a
  // frame so an origin change in the same update, which resets the strip to card 0 above, has
  // landed first.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !selectedCourseId) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const targetIndex = courses.findIndex((c) =>
        isSameCourse(c.id, selectedCourseId),
      );
      if (targetIndex < 0) {
        return;
      }
      const { nearestIndex } = findNearestCard(el);
      if (nearestIndex === targetIndex) {
        return;
      }
      const card = el.children[targetIndex] as HTMLElement | undefined;
      if (!card) {
        return;
      }
      slideTargetIndex.current = targetIndex;
      // The last few cards can never reach the left edge (the strip runs out of scroll first),
      // so the hold also lifts on its own once any slide has certainly finished.
      window.setTimeout(() => {
        if (slideTargetIndex.current === targetIndex) {
          slideTargetIndex.current = null;
        }
      }, 1000);
      el.scrollTo({
        left:
          el.scrollLeft +
          card.getBoundingClientRect().left -
          el.getBoundingClientRect().left,
        behavior: 'smooth',
      });
    });
    return (): void => window.cancelAnimationFrame(frame);
    // Only a new selection should move the strip. Re-running on `courses` would drag it back to
    // the selected card on every GPS fix while the visitor is swiping somewhere else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  const handleScroll = () => {
    updateScrollState();
    if (scrollFrame.current !== null) {
      return;
    }
    scrollFrame.current = window.requestAnimationFrame(() => {
      scrollFrame.current = null;
      selectNearestCard();
    });
  };

  // A queued frame firing after unmount would call onCourseClick on a dead component.
  useEffect(
    () => () => {
      if (scrollFrame.current !== null) {
        window.cancelAnimationFrame(scrollFrame.current);
      }
    },
    [],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    // Any touch or click on the strip is the visitor taking over, so a slide started by a pin tap
    // stops holding auto select back.
    slideTargetIndex.current = null;
    // Mouse only. A touch swipe is already a real scroll gesture, and running the manual
    // scrollLeft assignment below on top of it meant two things moved the strip at once: the
    // browser's own momentum scroll and this handler writing an absolute position computed
    // from where the finger started. The first swipe after a tap lost that fight and jumped a
    // whole card. Pen is treated like touch for the same reason.
    if (!el || event.pointerType !== 'mouse') {
      return;
    }
    dragState.current = {
      isDown: true,
      startX: event.clientX,
      startScrollLeft: el.scrollLeft,
      dragged: false,
    };
    setIsDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.isDown) {
      return;
    }
    const delta = event.clientX - dragState.current.startX;
    if (Math.abs(delta) > 3) {
      dragState.current.dragged = true;
    }
    el.scrollLeft = dragState.current.startScrollLeft - delta;
  };

  const endDrag = () => {
    if (!dragState.current.isDown) {
      return;
    }
    dragState.current.isDown = false;
    setIsDragging(false);
    if (dragState.current.dragged) {
      settleToNearestCard();
    }
  };

  // Suppress the click that would otherwise fire on the card right after a drag ends.
  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (dragState.current.dragged) {
      event.stopPropagation();
      dragState.current.dragged = false;
    }
  };

  if (courses.length === 0) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      // Phone only. On a desktop the map has room to be a map, and browsing nearby courses is
      // what the full-page list view is for — the strip there was a second, worse list sitting on
      // top of the thing the visitor came to look at. The card is 380px tall, so on a wide screen
      // it cost far more map than it earned.
      // Everything below sm is where this lives. On a phone the detail drawer opens full width
      // over it when a card is viewed; a pin tap brings the card up here instead.
      className="absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-charcoal via-charcoal/90 to-transparent pt-10 sm:hidden"
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
    >
      <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-primary/80 sm:px-4 sm:text-xs">
        Nearest to {originLabel}
      </p>

      <div className="group/strip relative">
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollByCards(-1)}
            aria-label="Scroll left"
            className="absolute left-1 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-primary/40 bg-charcoal/80 p-1.5 text-primary shadow-lg backdrop-blur transition-opacity hover:bg-charcoal sm:flex"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollByCards(1)}
            aria-label="Scroll right"
            className="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-primary/40 bg-charcoal/80 p-1.5 text-primary shadow-lg backdrop-blur transition-opacity hover:bg-charcoal sm:flex"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onClickCapture={handleClickCapture}
          className={`no-scrollbar flex cursor-grab gap-3 overflow-x-auto px-3 pb-1 active:cursor-grabbing sm:gap-4 sm:px-4 ${
            isDragging ? 'snap-none' : 'snap-x snap-mandatory'
          }`}
        >
          {courses.map((course) => (
            <CourseCompactCard
              key={course.id}
              course={course}
              isSelected={isSameCourse(course.id, selectedCourseId)}
              onView={() => onViewCourse(course)}
              isPlayed={isCoursePlayed(course)}
              onTogglePlayed={() => onTogglePlayed(course)}
              // Sizing only — the card itself is the same component the drawer's next-nearby row
              // renders. 85vw on a phone, so the next card peeks in at the edge and the strip
              // reads as scrollable without a hint or an arrow. Wider than the old 75vw because
              // this card is a single row: the name, country and address need the horizontal
              // space the hero used to take vertically. Capped from sm upward.
              className="w-[85vw] shrink-0 snap-start sm:w-80"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NearbyCoursesStrip;
